// Clips are played the moment they fall due, off the wall clock.
//
// They used to be handed to the Web Audio clock up to two minutes ahead, on the theory that the
// audio clock keeps perfect time even when timers are throttled. On a Windows machine running
// Edge that clock turned out to drift badly while nothing was playing - a few seconds at first,
// twenty within minutes - and announcements ran late by however far it had wandered. So the
// audio clock is now only trusted over the second or two it takes to queue one clip behind
// another, where drift does not matter.

type WindowWithLegacyAudioContext = Window & {
  webkitAudioContext?: typeof AudioContext
}

// Two rooms can be due in the same second; we play their announcements back to back instead of
// on top of each other.
const GAP_BETWEEN_ANNOUNCEMENTS_SECONDS = 0.4

// Breath between the chime and the voice within one announcement.
const GAP_AFTER_CHIME_SECONDS = 0.15

function getAudioContextConstructor(): typeof AudioContext | undefined {
  if (typeof window === "undefined") {
    return undefined
  }

  return window.AudioContext ?? (window as WindowWithLegacyAudioContext).webkitAudioContext
}

export class AnnouncementAudio {
  private context: AudioContext | null = null
  private gainNode: GainNode | null = null
  private readonly buffers = new Map<string, AudioBuffer>()

  // Wall-clock time the clips already sounding run until. Two rooms genuinely start at the same
  // minute, so a second clip waits for the first rather than talking over it.
  private busyUntil = 0

  private volume = 1

  static isSupported(): boolean {
    return getAudioContextConstructor() !== undefined
  }

  get isArmed(): boolean {
    return this.context !== null
  }

  // Browsers keep an AudioContext suspended until a user gesture, so this must be called from a
  // click handler before anything can be scheduled.
  async arm(): Promise<void> {
    if (!this.context) {
      const AudioContextConstructor = getAudioContextConstructor()

      if (!AudioContextConstructor) {
        throw new Error("This browser has no Web Audio support, announcements cannot be played.")
      }

      this.context = new AudioContextConstructor()
      this.gainNode = this.context.createGain()
      this.gainNode.gain.value = this.volume
      this.gainNode.connect(this.context.destination)
    }

    await this.ensureRunning()
  }

  // Some browsers suspend the context again when the page is hidden for a long time. Cheap enough
  // to call on every tick.
  async ensureRunning(): Promise<void> {
    if (this.context?.state === "suspended") {
      await this.context.resume()
    }
  }

  setVolume(volume: number): void {
    this.volume = volume

    if (this.gainNode) {
      this.gainNode.gain.value = volume
    }
  }

  async preload(url: string): Promise<void> {
    await this.load(url)
  }

  /**
   * Plays one announcement - the chime and then the voice - as soon as it can be heard: now, or
   * straight after whatever is still sounding. The parts are booked as one block, so another
   * room's announcement can never land between a chime and the words it belongs to.
   *
   * Returns the wall-clock moment it actually starts, which is `now` unless it had to wait.
   */
  async play(urls: string[]): Promise<number> {
    const context = this.context
    const gainNode = this.gainNode

    if (!context || !gainNode) {
      throw new Error("Announcement audio has not been armed yet.")
    }

    const buffers = await Promise.all(urls.map((url) => this.load(url)))

    const now = Date.now()
    const startsAt = Math.max(now, this.busyUntil)
    let offsetSeconds = (startsAt - now) / 1000

    buffers.forEach((buffer, index) => {
      const source = context.createBufferSource()
      source.buffer = buffer
      source.connect(gainNode)
      source.start(context.currentTime + offsetSeconds)

      offsetSeconds += buffer.duration
      if (index < buffers.length - 1) {
        offsetSeconds += GAP_AFTER_CHIME_SECONDS
      }
    })

    this.busyUntil = now + (offsetSeconds + GAP_BETWEEN_ANNOUNCEMENTS_SECONDS) * 1000

    return startsAt
  }

  private async load(url: string): Promise<AudioBuffer> {
    const cached = this.buffers.get(url)

    if (cached) {
      return cached
    }

    if (!this.context) {
      throw new Error("Announcement audio has not been armed yet.")
    }

    const response = await fetch(url)

    if (!response.ok) {
      throw new Error(`Announcement clip "${url}" could not be loaded (${response.status}).`)
    }

    const buffer = await this.context.decodeAudioData(await response.arrayBuffer())
    this.buffers.set(url, buffer)

    return buffer
  }
}
